// Lib
import { FC, useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
// Hooks
import { useViewport } from "hooks";
// Types
import { UploadVideoForm } from "./types";
// Components
import { Modal } from "components";
import { ImageUpload } from "components/Form";
import { VideoUpload } from "components/Form/VideoUpload";
// Styled
import { FlexContainer } from "styled/Box";
import { Button } from "styled/Buttons";

import { resolver } from "./validation";

interface VideoUploadModalProps {
  isLoading: boolean;
  open: boolean;
  handleClose: () => void;
  handleUpload: (data: { posterURL: string; videoURL: string }) => void;
}

export const VideoUploadModal: FC<VideoUploadModalProps> = ({
  open,
  isLoading,
  handleUpload,
  handleClose,
}) => {
  const { isMobile } = useViewport();

  const {
    setValue,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<UploadVideoForm>({ resolver });

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const onSubmit: SubmitHandler<UploadVideoForm> = async ({ image, video }) => {
    if (!image || !video) {
      return;
    }

    handleUpload({ posterURL: image, videoURL: video });
  };

  return (
    <Modal open={open} title="Upload Video" onClose={handleClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FlexContainer
          $gap={16}
          $column={isMobile}
          $width={isMobile ? "250px" : undefined}
          $margin="0 auto"
        >
          <ImageUpload
            label="Poster Image"
            value={watch("image")}
            error={errors?.image?.message}
            onChange={image => setValue("image", image)}
            accept={["jpeg", "jpg", "png", "webp"]}
            maxSize={5 * 1024} // 5MB
            disabled={isLoading}
            style={{
              height: "auto",
              width: "100%",
              maxWidth: isMobile ? "250px" : undefined,
              margin: "0 auto",
              aspectRatio: "1 / 1",
            }}
          />

          <VideoUpload
            label="Video File"
            value={watch("video")}
            error={errors?.video?.message}
            onChange={video => setValue("video", video)}
            accept={["video/mp4", "video/quicktime"]}
            maxSize={20 * 1024} // 5MB
            disabled={isLoading}
            style={{
              height: "auto",
              width: "100%",
              maxWidth: isMobile ? "250px" : undefined,
              margin: "0 auto",
              aspectRatio: "1 / 1",
            }}
          />
        </FlexContainer>

        <FlexContainer
          $fullwidth
          $align="center"
          $justify="flex-end"
          $gap={8}
          $margin="24px 0 0"
          $column={isMobile}
        >
          <Button.Heading
            $fullWidth={isMobile}
            disabled={isLoading}
            onClick={handleClose}
          >
            Cancel
          </Button.Heading>

          <Button.Heading
            $fullWidth={isMobile}
            loading={isLoading}
            type="primary"
            htmlType="submit"
          >
            Save
          </Button.Heading>
        </FlexContainer>
      </form>
    </Modal>
  );
};
